import { BaseElement, defineElement } from '@webtides/element-js';
import qs from 'qs/dist/qs';
import RetailerPlacesInputEvents from '../retailer-places-input/RetailerPlacesInputEvents';
import RetailerSearchEvents from './RetailerSearchEvents';
import BuyBoxEvents from '../../_buy-box/buy-box/BuyBoxEvents';
import ModalElementEvent from '../../../elements/_modal/modal-element/ModalElementEvent';
import { parseAttribute } from '@webtides/element-js/src/util/AttributeParser';

const SITE_NAME = window.siteConfig()?.siteName;

class RetailerSearch extends BaseElement {
    searchMeta = { page: 0, nextPage: 1, previousPage: 0, pages: 0, total: 0 };
    additionalData = '';
    #modal = null;

    constructor() {
        super({ propertyOptions: { fetchingRetailers: { notify: true } } });
    }

    connected() {
        this.#modal = this.closest('modal-element');
        this.#modal.addEventListener(ModalElementEvent.OPEN, () => this.setFilter());
    }

    properties() {
        return {
            url: '/.rest/dealer/search/location',
            fetchingRetailers: false,
            locationSearchString: '',
            countryCode: 'de',
            brand: '',
            page: 1,
            pageSize: 10,
            retailers: [],
            withVariant: false,
            selectedFilter: '',
        };
    }

    setFilter() {
        this.$refs.filterOptions?.forEach(filterOption => {
            if (this.#modal.parameters?.selectedFilter === filterOption.id) {
                filterOption.setAttribute('checked', 'true');
                this.selectedFilter = filterOption.value;
            } else if (filterOption.hasAttribute('checked') && parseAttribute(filterOption.getAttribute('checked'))) {
                this.selectedFilter = filterOption.value;
            }
        });
    }

    get canSearch() {
        return this.locationSearchString !== '';
    }

    watch() {
        return {
            fetchingRetailers: fetchingRetailers => {
                if (fetchingRetailers === true) {
                    this.setVisibilities({
                        initialContent: false,
                        loadingSpinner: true,
                    });
                } else {
                    const isAvailable = this.$refs.availabilitySearchVariant?.variant?.available;
                    const isWareHouseAvailable = this.$refs.availabilitySearchVariant?.variant?.hasCentralStock;
                    const hasRetailers = this.retailers.length > 0;

                    this.setVisibilities({
                        loadingSpinner: false,
                        availabilitySearchVariantButton: isAvailable,
                        warehouseAvailabilityNotice: isWareHouseAvailable,
                        successContent: hasRetailers,
                        errorContent: !hasRetailers,
                        searchFilters: true, // displays always filter options, if retailer list is empty
                        moreBtn: hasRetailers && this.searchMeta.page < this.searchMeta.pages,
                    });
                }

                this.dispatch(RetailerSearchEvents.FETCHING_RETAILERS_CHANGED, fetchingRetailers);
            },
            retailers: retailers => {
                this.$refs.list.retailers = retailers;
            },
            locationSearchString: async searchString => {
                const isAvailable = this.$refs.availabilitySearchVariant?.variant?.available;
                if (searchString === '') {
                    this.$refs.retailerPlacesInput.clear();
                    this.setVisibilities({
                        errorContent: false,
                        searchInputBox: true,
                        searchInfoBox: false,
                    });
                } else {
                    this.setVisibilities({
                        searchInputBox: false,
                        errorContent: false,
                        searchInfoBox: true,
                        initialContent: false,
                        searchFilters: true,
                    });
                    await this.fetchRetailers();
                    this.$refs.searchInfoBox.searchString = this.additionalData;
                }
            },
        };
    }

    events() {
        return {
            this: {
                [RetailerPlacesInputEvents.PLACES_INPUT_CHANGE]: e => {
                    if (this.locationSearchString !== '') {
                        // We have changed the search string. The page should be reset.
                        this.page = 1;
                    }

                    // Update the {locationSearchString} to fire a new retailer search.
                    this.locationSearchString = e.detail.searchString;
                },
            },
            '[data-change-input]': {
                click: () => {
                    this.locationSearchString = '';
                },
            },
            '[ref="moreBtn"]': {
                click: event => {
                    this.fetchRetailers(true);
                },
            },
            '[add-to-cart]': {
                click: () => {
                    this.#modal?.close();
                    this.dispatch(BuyBoxEvents.ADD_TO_CART, this.$refs.availabilitySearchVariant?.variant, true);
                },
            },
            '[ref="filterOptions[]"]': {
                change: event => {
                    this.selectedFilter = event.target.value;
                    // We have changed the filter. The page should be reset.
                    this.page = 1;
                    // Fetch new retailer list
                    this.fetchRetailers();
                },
            },
        };
    }

    setVisibilities(visibilities) {
        Object.entries(visibilities).forEach(([key, visibility]) => {
            if (visibility === true) {
                this.$refs[key]?.classList.remove('hidden');
            } else {
                this.$refs[key]?.classList.add('hidden');
            }
        });
        if (visibilities.searchInputBox) {
            this.$refs.retailerPlacesInput.focus();
        }
    }

    async fetchRetailers(incrementPage) {
        if (this.fetchingRetailers) {
            return;
        }

        if (incrementPage) {
            this.page = this.page + 1;
        }

        const parameters = {
            location: this.locationSearchString,
            countrycode: this.countryCode,
            page: this.page,
            pageSize: this.pageSize,
            brand: this.brand,
            siteName: SITE_NAME,
            shop: this.selectedFilter === 'shop',
            ...(this.selectedFilter !== 'brand' &&
                this.selectedFilter !== 'shop' && { competencies: `${this.selectedFilter}` }),
        };

        if (this.withVariant) {
            parameters.sku = this.$refs.availabilitySearchVariant?.variant?.sku;
        }

        const url = `${this.url}?${qs.stringify(parameters)}`;

        this.fetchingRetailers = true;

        try {
            let response = await fetch(url);
            if (!response.ok) {
                throw Error(response.statusText);
            }

            response = await response.json();
            this.searchMeta = response.meta;
            this.additionalData = response.additionalData;

            const retailers = response.data;

            if (this.searchMeta.page > 1) {
                // append more retailers
                this.retailers = [...this.retailers, ...retailers];
            } else {
                this.retailers = retailers;
            }
        } catch (e) {
            this.reset();
        }
        this.fetchingRetailers = false;
    }

    reset() {
        this.searchMeta = { page: 0, nextPage: 1, previousPage: 0, pages: 0, total: 0 };
        this.retailers = [];
    }
}

defineElement('retailer-search', RetailerSearch);
